import {
  type BaseEditor,
  type Editor,
  type Element,
  type Text,
  Transforms,
} from 'slate'
import { jsx } from 'slate-hyperscript'

export type HTMLEditor = {
  htmlExtensionInstalled?: true
}

const ELEMENT_TAGS = {
  A: (el) => ({ type: 'link', url: el.getAttribute('href') }),
  BLOCKQUOTE: () => ({ type: 'block-quote' }),
  TITLE: () => ({ type: 'heading-one' }),
  H1: () => ({ type: 'heading-two' }),
  H2: () => ({ type: 'heading-three' }),
  H3: () => ({ type: 'heading-three' }),
  H4: () => ({ type: 'heading-three' }),
  H5: () => ({ type: 'heading-three' }),
  IMG: (el) => ({ type: 'image', url: el.getAttribute('src') }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  UL: () => ({ type: 'bulleted-list' }),
} as const satisfies {
  [tag: string]: (el: HTMLElement) => Omit<Element, 'children'>
} // children are added later on

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS = {
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  STRONG: () => ({ bold: true }),
} as const satisfies { [tag: string]: (el: HTMLElement) => Omit<Text, 'text'> } // children are added later on

type ProcessedNode = Element | Text | string

export function deserialize(
  el: HTMLElement | Node,
  trim = false,
): ProcessedNode | ProcessedNode[] {
  if (el.nodeType === Node.TEXT_NODE) {
    return (trim ? el.textContent?.trim() : el.textContent) || []
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return trim ? [] : ''
  } else if (el.nodeName === 'BR') {
    return '\n'
  }

  const { nodeName, childNodes } = el

  if (nodeName === 'TABLE') {
    return jsx('element', { type: 'table' }, deserializeTableNodes(childNodes))
  }

  let children = Array.from(childNodes).flatMap((el) => deserialize(el, trim))
  if (children.length === 0) children = [{ text: '' }]

  if (nodeName === 'BODY') {
    return jsx('fragment', {}, children)
  }

  if (nodeName in ELEMENT_TAGS) {
    const attrs = ELEMENT_TAGS[nodeName as keyof typeof ELEMENT_TAGS](
      el as HTMLElement,
    )
    return jsx('element', attrs, children)
  }

  if (nodeName in TEXT_TAGS) {
    const attrs = TEXT_TAGS[nodeName as keyof typeof TEXT_TAGS]()
    return children.map((child: any) => jsx('text', attrs, child))
  }

  if (nodeName === 'STYLE' || nodeName === 'SCRIPT') {
    return []
  }

  return children
}

export function withHtml<T extends BaseEditor>(editor: T) {
  const e = editor as T & Editor
  const { insertData } = e

  e.htmlExtensionInstalled = true

  e.insertData = (data) => {
    if (data.types.includes('application/x-slate-fragment')) {
      insertData(data)
      return
    }

    const html = data.getData('text/html')

    if (!html) {
      insertData(data)
      return
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html')
    const fragment = deserialize(parsed.body)
    Transforms.insertFragment(
      e,
      jsx('fragment', {}, fragment as Element[], jsx('text', {}, '')),
    )

    return
  }

  return e
}

function deserializeTableNodes(nodes: NodeListOf<ChildNode>): Element[] {
  return Array.from(nodes).flatMap((child) => {
    if (child instanceof HTMLElement) {
      switch (child.nodeName) {
        case 'TBODY':
        case 'THEAD':
        case 'TFOOT':
          return deserializeTableNodes(child.childNodes)
        case 'TR':
          return jsx(
            'element',
            { type: 'table-row' },
            deserializeTableNodes(child.childNodes),
          )
        case 'TD':
        case 'TH':
          return jsx(
            'element',
            { type: 'table-cell' },
            Array.from(child.childNodes)
              .flatMap((el) => deserialize(el, true))
              .filter((el) => {
                if (typeof el === 'string' && el.trim() === '') return false
                if (
                  typeof el === 'object' &&
                  'text' in el &&
                  el.text.trim() === ''
                )
                  return false
                return true
              }),
          )
      }
    }
    return []
  })
}
